import { useIsMobile } from '@/hooks/useIsMobile'

/**
 * Structural shape for anything team-like across the app. Many payloads use
 * snake_case `short_code`; camelCase `shortCode` payloads (Game.homeTeam etc.)
 * should be adapted inline at the call site: `{ name: g.homeTeam.name,
 * short_code: g.homeTeam.shortCode }`.
 */
export interface TeamLike {
  name: string
  short_code?: string | null
}

export type TeamDisplayVariant = 'full' | 'short' | 'auto'

/** Filler words dropped when deriving initials from a multi-word name. */
const FILLER_WORDS = new Set(['the', 'of', 'and', '&', 'fc', 'cf'])

/**
 * Derive a reasonable abbreviation from a full team name when short_code
 * is missing. Multi-word names → initials of significant words (drop
 * filler words like "the", "of", "&"), capped at 4 chars. Single-word
 * names → first 3 chars uppercased. Never returns empty for a non-empty
 * name.
 *   "Los Angeles Lakers" → "LAL"
 *   "Heat"               → "HEA"
 *   "Real Madrid CF"     → "RMC"
 */
export function deriveTeamCode(name: string): string {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return ''

  const words = trimmed.split(/\s+/)

  // Single-word name → first 3 chars uppercased.
  if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase()
  }

  // Multi-word → initials of significant words, dropping filler words.
  const significant = words.filter((w) => !FILLER_WORDS.has(w.toLowerCase()))
  // If dropping filler words leaves nothing, don't drop them.
  const source = significant.length > 0 ? significant : words

  const initials = source
    .map((w) => {
      // Skip leading punctuation so "(TBD)" → "T", not "(".
      const match = w.match(/[a-zA-Z0-9]/)
      return match ? match[0] : ''
    })
    .join('')
    .toUpperCase()
    .slice(0, 4)

  // Edge case: multi-word name with no alphanumerics anywhere.
  if (!initials) return trimmed.slice(0, 3).toUpperCase()

  return initials
}

/**
 * Resolve a team's display string.
 *  - 'full'  → team.name
 *  - 'short' → team.short_code (uppercased) if set, else deriveTeamCode(name)
 *  - 'auto'  → 'short' when isMobile, 'full' otherwise
 * isMobile only matters for 'auto'; defaults false so non-component
 * callers (share cards) can ignore it.
 */
export function getTeamDisplay(
  team: TeamLike,
  variant: TeamDisplayVariant,
  isMobile = false
): string {
  if (variant === 'auto') {
    return getTeamDisplay(team, isMobile ? 'short' : 'full')
  }

  if (variant === 'short') {
    const code = team.short_code?.trim()
    if (code) return code.toUpperCase()
    return deriveTeamCode(team.name)
  }

  // 'full'
  return team.name
}

/**
 * Hook form for components. Binds the current viewport once via the
 * existing useIsMobile() hook and returns a resolver so callers just
 * write display(team, 'auto').
 */
export function useTeamDisplay(): (
  team: TeamLike,
  variant: TeamDisplayVariant
) => string {
  const isMobile = useIsMobile()
  return (team: TeamLike, variant: TeamDisplayVariant) =>
    getTeamDisplay(team, variant, isMobile)
}
